import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PropertyDetailsTab = ({ formData, onChange, validationErrors, onBlur }) => {
  const errors = validationErrors || {};
  const customFields = [];
  
  const propertyTypes = [
    { value: 'apartment', label: 'شقة' },
    { value: 'villa', label: 'فيلا' },
    { value: 'office', label: 'مكتب' },
    { value: 'shop', label: 'محل تجاري' },
    { value: 'land', label: 'أرض' },
    { value: 'warehouse', label: 'مستودع' }
  ];

  const purposeOptions = [
    { value: 'sale', label: 'للبيع' },
    { value: 'rent', label: 'للإيجار' },
    { value: 'both', label: 'للبيع والإيجار' }
  ];

  const furnishingOptions = [
    { value: 'furnished', label: 'مفروش' },
    { value: 'semi-furnished', label: 'نصف مفروش' },
    { value: 'unfurnished', label: 'غير مفروش' }
  ];

  const features = [
    { id: 'parking', label: 'موقف سيارات' },
    { id: 'elevator', label: 'مصعد' },
    { id: 'garden', label: 'حديقة' },
    { id: 'pool', label: 'مسبح' },
    { id: 'gym', label: 'صالة رياضية' },
    { id: 'security', label: 'حراسة أمنية' },
    { id: 'centralAC', label: 'تكييف مركزي' },
    { id: 'balcony', label: 'شرفة' }
  ];

  const detailsCustomFields = customFields?.filter(field => field?.category === 'details');

  const renderCustomField = (field) => {
    const value = formData?.customFields?.[field?.id] || '';
    const error = errors?.[`customFields.${field?.id}`];

    switch (field?.type) {
      case 'textarea':
        return (
          <div key={field?.id} className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              {field?.label} {field?.required && <span className="text-error">*</span>}
            </label>
            <textarea
              className="w-full min-h-24 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={`أدخل ${field?.label}`}
              value={value}
              onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            />
            {error && <p className="text-sm text-error mt-1">{error}</p>}
          </div>
        );
      case 'checkbox':
        return (
          <div key={field?.id} className="flex items-center">
            <Checkbox
              label={field?.label}
              checked={value === true || value === 'true'}
              onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.checked)}
            />
          </div>
        );
      case 'select':
        return (
          <Select
            key={field?.id}
            label={field?.label}
            options={field?.options || []}
            value={value}
            onChange={(val) => onChange(`customFields.${field?.id}`, val)}
            error={error}
            required={field?.required}
          />
        );
      default:
        return (
          <Input
            key={field?.id}
            label={field?.label}
            type={field?.type || 'text'}
            placeholder={`أدخل ${field?.label}`}
            value={value}
            onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            error={error}
            required={field?.required}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <Input
          label="عنوان العقار"
          type="text"
          placeholder="أدخل عنوان العقار"
          value={formData?.title}
          onChange={(e) => onChange('title', e?.target?.value)}
          onBlur={() => onBlur?.('title')}
          error={errors?.title}
          required
        />

        <Input
          label="رقم العقار"
          type="text"
          placeholder="يتم إنشاؤه تلقائياً"
          value={formData?.propertyId}
          disabled
        />

        <Select
          label="نوع العقار"
          options={propertyTypes}
          value={formData?.propertyType}
          onChange={(value) => onChange('propertyType', value)}
          error={errors?.propertyType}
          required
        />

        <Select
          label="الغرض"
          options={purposeOptions}
          value={formData?.purpose}
          onChange={(value) => onChange('purpose', value)}
          error={errors?.purpose}
          required
        />

        <Input
          label="عدد الغرف"
          type="number"
          placeholder="0"
          value={formData?.bedrooms}
          onChange={(e) => onChange('bedrooms', e?.target?.value)}
          onBlur={() => onBlur?.('bedrooms')}
          min="0"
        />

        <Input
          label="عدد الحمامات"
          type="number"
          placeholder="0"
          value={formData?.bathrooms}
          onChange={(e) => onChange('bathrooms', e?.target?.value)}
          onBlur={() => onBlur?.('bathrooms')}
          min="0"
        />

        <Input
          label="المساحة (متر مربع)"
          type="number"
          placeholder="0"
          value={formData?.area}
          onChange={(e) => onChange('area', e?.target?.value)}
          onBlur={() => onBlur?.('area')}
          error={errors?.area}
          required
          min="0"
        />

        <Input
          label="رقم الطابق"
          type="number"
          placeholder="0"
          value={formData?.floor}
          onChange={(e) => onChange('floor', e?.target?.value)}
          min="0"
        />

        <Select
          label="حالة التأثيث"
          options={furnishingOptions}
          value={formData?.furnishing}
          onChange={(value) => onChange('furnishing', value)}
        />

        <Input
          label="سنة البناء"
          type="number"
          placeholder="2026"
          value={formData?.yearBuilt}
          onChange={(e) => onChange('yearBuilt', e?.target?.value)}
          min="1900"
          max="2026"
        />

        <Input
          label="عمر العقار (بالسنوات)"
          type="number"
          placeholder="0"
          value={formData?.propertyAge}
          onChange={(e) => onChange('propertyAge', e?.target?.value)}
          min="0"
          description="عمر العقار منذ البناء أو آخر تجديد"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          الوصف <span className="text-error">*</span>
        </label>
        <textarea
          className="w-full min-h-24 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="أدخل وصفاً تفصيلياً للعقار..."
          value={formData?.description}
          onChange={(e) => onChange('description', e?.target?.value)}
        />
        {errors?.description && (
          <p className="text-sm text-error mt-1">{errors?.description}</p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          المميزات والخدمات
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {features?.map((feature) => (
            <Checkbox
              key={feature?.id}
              label={feature?.label}
              checked={formData?.features?.includes(feature?.id) || false}
              onChange={(e) => {
                const currentFeatures = formData?.features || [];
                const newFeatures = e?.target?.checked
                  ? [...currentFeatures, feature?.id]
                  : currentFeatures?.filter(f => f !== feature?.id);
                onChange('features', newFeatures);
              }}
            />
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          ملاحظات إضافية
        </label>
        <textarea
          className="w-full min-h-20 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="أي ملاحظات أو تفاصيل إضافية..."
          value={formData?.notes}
          onChange={(e) => onChange('notes', e?.target?.value)}
        />
      </div>

      {detailsCustomFields?.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <span className="w-1 h-4 bg-primary rounded"></span>
            الحقول المخصصة
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {detailsCustomFields?.map(field => renderCustomField(field))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetailsTab;